import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useCurrency } from '../../context/CurrencyContext';
import { ArrowLeft, ChevronDown, ChevronUp, ShoppingBag } from 'lucide-react';
import collectionsService from '../../services/collectionsService';
import productsService from '../../services/productsService';
import logger from '../../utils/logging';
import analyticsService from '../../services/analyticsService';
import PLACEHOLDER_IMAGE from '../../utils/placeholderImage';
import { productDetailPath } from '../../utils/productPath';

const Collection = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { formatPrice } = useCurrency();
  
  const [collection, setCollection] = useState(null);
  const [collectionProducts, setCollectionProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedProducts, setExpandedProducts] = useState(new Set()); // Track which products are expanded

  useEffect(() => {
    const loadCollection = async () => {
      try {
        logger.logComponentMount('Collection', { slug });
        console.log('🔍 Collection slug from URL:', slug);
        
        if (!slug) {
          console.log('❌ No collection slug provided');
          setLoading(false);
          return;
        }
        
        // Get the collection
        const foundCollection = await collectionsService.getCollection(slug);
        
        if (!foundCollection) {
          console.log('❌ Collection not found:', slug);
          setTimeout(() => navigate('/gallery'), 2000);
          return;
        }
        
        setCollection(foundCollection);
        console.log('✅ Collection loaded:', foundCollection);
        logger.log('info', 'navigation', 'Collection viewed', {
          collectionId: foundCollection.id,
          collectionName: foundCollection.name
        });
        analyticsService.trackEvent('collection_view', {
          collectionId: foundCollection.id,
          collectionName: foundCollection.name,
          source: 'collection_page'
        });
        
        const slugLower = (slug || '').toLowerCase();
        if (slugLower === 'printful-merch') {
          await loadPrintfulPodProducts();
        } else if (foundCollection.productIds && foundCollection.productIds.length > 0) {
          await loadCollectionProducts(foundCollection.productIds);
        }
        
      } catch (error) {
        console.error('Error loading collection:', error);
        logger.logError(error, 'Collection component');
        setTimeout(() => navigate('/gallery'), 2000);
      } finally {
        setLoading(false);
      }
    };

    loadCollection();
  }, [slug, navigate]);

  /** All print-on-demand (Printful-backed) products for this shop — single source of truth in API. */
  const loadPrintfulPodProducts = async () => {
    try {
      console.log('🚀 [COLLECTION] Loading Printful POD products...');
      const allProducts = await productsService.getProducts({
        forceRefresh: true,
        productType: 'print_on_demand',
      });
      const list = Array.isArray(allProducts) ? allProducts : [];
      const productsWithDetails = await Promise.all(
        list.map(async (product) => {
          if (product.folder) {
            try {
              const fullProduct = await productsService.getProduct(product.folder);
              return fullProduct || product;
            } catch (error) {
              console.error(`Failed to load full details for ${product.name}:`, error);
              return product;
            }
          }
          return product;
        })
      );
      setCollectionProducts(productsWithDetails);
      console.log('✅ [COLLECTION] POD products loaded:', productsWithDetails.length);
    } catch (error) {
      console.error('❌ [COLLECTION] Failed to load POD products:', error);
      logger.logError(error, 'Collection loadPrintfulPodProducts');
    }
  };

  const loadCollectionProducts = async (productIds) => {
    try {
      console.log('🚀 [COLLECTION] Loading products for collection...');
      console.log('📊 [COLLECTION] Product IDs:', productIds);
      
      // Get all products and filter to those in the collection
      const allProducts = await productsService.getProducts();
      const products = allProducts.filter(p => productIds.includes(p.id));
      
      // For each product, load full details if needed
      const productsWithDetails = await Promise.all(
        products.map(async (product) => {
          if (product.folder) {
            try {
              const fullProduct = await productsService.getProduct(product.folder);
              return fullProduct || product;
            } catch (error) {
              console.error(`Failed to load full details for ${product.name}:`, error);
              return product;
            }
          }
          return product;
        })
      );
      
      console.log('✅ [COLLECTION] Products loaded:', productsWithDetails.length);
      setCollectionProducts(productsWithDetails);
    } catch (error) {
      console.error('❌ [COLLECTION] Failed to load collection products:', error);
      logger.logError(error, 'Collection loadCollectionProducts');
    }
  };

  const toggleProductExpansion = (productId) => {
    console.log('🔄 [COLLECTION] Toggling product expansion:', productId);
    const newExpanded = new Set(expandedProducts);
    if (newExpanded.has(productId)) {
      newExpanded.delete(productId);
    } else {
      newExpanded.add(productId);
    }
    setExpandedProducts(newExpanded);
  };

  const handleAddToCart = (product, variant, event) => {
    event.stopPropagation(); // Prevent expanding/collapsing when clicking add to cart
    
    console.log('🛒 [COLLECTION] Add to cart clicked:', {
      productName: product.name,
      variantName: variant.name || variant.size,
      variantPrice: variant.price
    });
    
    logger.logButtonClick('Add to Cart', { 
      productId: product.id, 
      productName: product.name,
      variant: variant.name || variant.size,
      price: variant.price,
      source: 'collection_page'
    });
    
    // Get the correct image URL - Priority: primaryImage > images[0] > customImage > folder > placeholder
    const imageUrl = product.primaryImage || 
                     (product.images && product.images.length > 0 ? product.images[0] : null) ||
                     product.customImage ||
                     product.imageUrl ||
                     (product.folder ? `/products/${product.folder}/custom-0.jpg` : null) ||
                     PLACEHOLDER_IMAGE;
    
    addToCart({
      id: `${product.id}-${variant.variantId || variant.id}`,
      title: `${product.name} / ${variant.size || variant.name}`,
      image: imageUrl,
      price: parseFloat(variant.price.toString().replace(/[^0-9.]/g, '')),
      variant: variant.color || variant.size || 'default',
      size: variant.size || 'default',
      type: 'product',
      productId: product.id,
      variantId: variant.variantId || variant.id,
      printfulVariantId: variant.printfulVariantId,
      printfulProductId: product.printfulData?.printfulProductId,
      printful_data: {
        variant_id: variant.printfulVariantId,
        files: variant.order_data?.files || product.variants?.[0]?.order_data?.files || [{
          type: "front",
          url: "No image available"
        }]
      }
    });
    
    logger.logCartAction('add_item', product.id, product.name, {
      variant: variant.name || variant.size,
      price: variant.price,
      source: 'collection_page'
    });
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'radial-gradient(ellipse at center, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: '80px'
      }}>
        <div style={{ textAlign: 'center', color: 'white' }}>
          <p style={{ fontSize: '18px' }}>Loading collection...</p>
        </div>
      </div>
    );
  }

  if (!collection) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'radial-gradient(ellipse at center, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: '80px'
      }}>
        <div style={{ textAlign: 'center', color: 'white' }}>
          <p style={{ fontSize: '18px', marginBottom: '16px' }}>Collection not found</p>
          <button
            onClick={() => navigate('/gallery')}
            style={{
              background: 'linear-gradient(135deg, #ec4899, #8b5cf6)',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            Back to Gallery
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'radial-gradient(ellipse at center, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)',
      paddingTop: '80px'
    }}>
      <div style={{
        maxWidth: '1200px',
        width: '100%',
        boxSizing: 'border-box',
        padding: window.innerWidth <= 768 ? '32px 16px' : '32px 24px',
        margin: '0 auto',
        position: 'relative',
        zIndex: 10
      }}>
        {/* Back Button */}
        <button
          onClick={() => navigate('/gallery')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: 'white',
            padding: '12px 16px',
            borderRadius: '8px',
            cursor: 'pointer',
            marginBottom: '32px',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.1)';
          }}
        >
          <ArrowLeft size={20} />
          Back to Gallery
        </button>

        {/* Collection Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '48px'
        }}>
          {collection.imageUrl && (
            <img
              src={collection.imageUrl}
              alt={collection.name}
              style={{
                width: '100%',
                maxWidth: '600px',
                height: '300px',
                objectFit: 'cover',
                borderRadius: '16px',
                marginBottom: '24px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
              }}
            />
          )}
          <h1 style={{
            fontSize: '3rem',
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #ec4899, #8b5cf6, #06b6d4)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '16px'
          }}>
            {collection.name}
          </h1>
          {collection.description && (
            <p style={{
              fontSize: '1.25rem',
              color: '#d1d5db',
              maxWidth: '800px',
              margin: '0 auto',
              lineHeight: '1.6'
            }}>
              {collection.description}
            </p>
          )}
        </div>

        {/* Products List */}
        {collectionProducts.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            color: '#9ca3af'
          }}>
            <p style={{ fontSize: '1.2rem', marginBottom: '16px' }}>
              This collection doesn't have any products yet
            </p>
            <Link
              to="/gallery"
              style={{
                color: '#8b5cf6',
                textDecoration: 'none',
                fontSize: '1rem'
              }}
            >
              Browse all products →
            </Link>
          </div>
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px'
          }}>
            {collectionProducts.map((product) => {
              const isExpanded = expandedProducts.has(product.id);
              // Priority: primaryImage > images[0] > customImage > folder > placeholder
              const productImageUrl = product.primaryImage || 
                                      (product.images && product.images.length > 0 ? product.images[0] : null) ||
                                      product.customImage ||
                                      product.imageUrl ||
                                      (product.folder ? `/products/${product.folder}/custom-0.jpg` : null) ||
                                      PLACEHOLDER_IMAGE;
              
              return (
                <div
                  key={product.id}
                  style={{
                    background: 'rgba(0, 0, 0, 0.6)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {/* Product Header (Clickable) */}
                  <div
                    onClick={() => toggleProductExpansion(product.id)}
                    style={{
                      padding: '24px',
                      cursor: 'pointer',
                      display: 'flex',
                      gap: '24px',
                      alignItems: 'center',
                      transition: 'background 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <img
                      src={productImageUrl}
                      alt={product.name}
                      style={{
                        width: '120px',
                        height: '120px',
                        objectFit: 'cover',
                        borderRadius: '8px',
                        flexShrink: 0
                      }}
                      onError={(e) => {
                        console.error('❌ [Collection] Image failed to load:', e.target.src);
                        // Fallback chain for Mango products
                        if (product.images && product.images.length > 1) {
                          const currentIndex = product.images.indexOf(e.target.src);
                          if (currentIndex !== -1 && currentIndex < product.images.length - 1) {
                            e.target.src = product.images[currentIndex + 1];
                            return;
                          }
                        }
                        // Use placeholder constant
                        if (e.target.src !== PLACEHOLDER_IMAGE) {
                          e.target.src = PLACEHOLDER_IMAGE;
                        }
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <h3 style={{
                        fontSize: '1.5rem',
                        fontWeight: '600',
                        color: 'white',
                        marginBottom: '8px'
                      }}>
                        {product.name}
                      </h3>
                      {product.shortDescription && (
                        <p style={{
                          color: '#9ca3af',
                          fontSize: '14px',
                          marginBottom: '8px',
                          lineHeight: '1.5'
                        }}>
                          {product.shortDescription}
                        </p>
                      )}
                      {product.price && (
                        <div style={{
                          color: '#8b5cf6',
                          fontSize: '1.1rem',
                          fontWeight: '600'
                        }}>
                          From {formatPrice(product.price)}
                        </div>
                      )}
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <span style={{
                        color: '#9ca3af',
                        fontSize: '14px'
                      }}>
                        {product.variants?.length || 0} variant{product.variants?.length !== 1 ? 's' : ''}
                      </span>
                      {isExpanded ? (
                        <ChevronUp size={24} color="#8b5cf6" />
                      ) : (
                        <ChevronDown size={24} color="#8b5cf6" />
                      )}
                    </div>
                  </div>

                  {/* Expanded Variants Section */}
                  {isExpanded && product.variants && product.variants.length > 0 && (
                    <div style={{
                      padding: '0 24px 24px 24px',
                      borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                      marginTop: '0'
                    }}>
                      <div style={{
                        marginTop: '24px',
                        display: 'grid',
                        gridTemplateColumns: window.innerWidth <= 768 
                          ? 'repeat(auto-fill, minmax(150px, 1fr))' 
                          : 'repeat(auto-fill, minmax(200px, 1fr))',
                        gap: '16px',
                        width: '100%',
                        boxSizing: 'border-box'
                      }}>
                        {product.variants.map((variant, index) => {
                          const variantImageUrl = variant.previewImage && product.folder
                            ? `/products/${product.folder}/${variant.previewImage}`
                            : productImageUrl;
                          
                          return (
                            <div
                              key={variant.id || index}
                              style={{
                                background: 'rgba(255, 255, 255, 0.05)',
                                borderRadius: '12px',
                                padding: '16px',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                                e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.5)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                              }}
                            >
                              <img
                                src={variantImageUrl}
                                alt={`${product.name} - ${variant.size || variant.name}`}
                                style={{
                                  width: '100%',
                                  height: '150px',
                                  objectFit: 'cover',
                                  borderRadius: '8px',
                                  marginBottom: '12px'
                                }}
                                onError={(e) => {
                                  e.target.src = productImageUrl || PLACEHOLDER_IMAGE;
                                }}
                              />
                              <div style={{
                                marginBottom: '8px'
                              }}>
                                <div style={{
                                  color: 'white',
                                  fontWeight: '600',
                                  fontSize: '14px',
                                  marginBottom: '4px'
                                }}>
                                  {variant.size || variant.name}
                                </div>
                                {variant.color && (
                                  <div style={{
                                    color: '#9ca3af',
                                    fontSize: '12px'
                                  }}>
                                    {variant.color}
                                  </div>
                                )}
                              </div>
                              <div style={{
                                color: '#8b5cf6',
                                fontWeight: '600',
                                fontSize: '16px',
                                marginBottom: '12px'
                              }}>
                                {formatPrice(variant.price)}
                              </div>
                              <button
                                onClick={(e) => handleAddToCart(product, variant, e)}
                                style={{
                                  width: '100%',
                                  padding: '10px 16px',
                                  background: 'linear-gradient(135deg, #ec4899, #8b5cf6)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  fontWeight: '600',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '8px',
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.transform = 'scale(1.05)';
                                  e.target.style.boxShadow = '0 4px 12px rgba(139, 92, 246, 0.4)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.transform = 'scale(1)';
                                  e.target.style.boxShadow = 'none';
                                }}
                              >
                                <ShoppingBag size={16} />
                                Add to Cart
                              </button>
                            </div>
                          );
                        })}
                      </div>
                      <div style={{
                        marginTop: '24px',
                        paddingTop: '24px',
                        borderTop: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <Link
                          to={productDetailPath(product)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            color: '#8b5cf6',
                            textDecoration: 'none',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                        >
                          View Full Product Details →
                        </Link>
                      </div>
                    </div>
                  )}

                  {/* No Variants Message */}
                  {isExpanded && (!product.variants || product.variants.length === 0) && (
                    <div style={{
                      padding: '24px',
                      borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                      textAlign: 'center',
                      color: '#9ca3af'
                    }}>
                      <p style={{ marginBottom: '16px' }}>This product has no variants available</p>
                      <Link
                        to={productDetailPath(product)}
                        style={{
                          color: '#8b5cf6',
                          textDecoration: 'none',
                          fontSize: '14px',
                          fontWeight: '500'
                        }}
                      >
                        View Product Details →
                      </Link>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Collection;

